import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

// models
import { SelectItem } from '@core/models/select-item.model';
import { RecordingConfig } from '@core/models/recording-config.model';
import { NotificationData } from "@core/store/models/notifications.interface";
// services
import { RecorderService } from '@core/services/recorder/recorder.service';

@Component({
    selector: 'core-layout-modal-record',
    templateUrl: './modal-record.component.html',
    styleUrls: ['./modal-record.component.scss']
})
/**
 * @input header is the title of the modal, there is a default
 * @input text is the message under the title, there is a default
 * @input buttonText is the main white button text
 * @input leftButtonText is the left gray button text, 
 *        if empty, only one button shown
 * @input disabled: in case of we want to disable the main button from parent
 * 
 * @output action main button action emitter
 * @output leftButtonAction left button action emitter
 * 
 */
export class LayoutModalRecordComponent implements OnInit {
    public readonly STATUS_DEVICES_LOADING = 1;
    public readonly STATUS_DEVICES_READY = 2;
    public readonly STATUS_DEVICES_ERROR = 3;
    public readonly STATUS_UNSUPPORTED_BROWSER = 4;

    @Input() text: string = '';
    @Input() header: string = '';
    @Input() buttonText: string;
    @Input() leftButtonText: string = "";
    @Input() disabled: boolean = false;
    @Input() notificationData: NotificationData = null;

    @Output() action = new EventEmitter<boolean>();
    @Output() leftButtonAction = new EventEmitter<boolean>();

    public status: number = this.STATUS_DEVICES_LOADING;
    // available sources on selectors
    public videoSources: Array<SelectItem> = [{
        title: "SCREEN",
        value: "screen",
        selected: true
    }];
    public audioSources: Array<SelectItem> = null;
    // system available media
    protected microphones: MediaDeviceInfo[] = [];
    protected cameras: MediaDeviceInfo[] = [];
    // configuration to set to start recording
    protected recordConfig: RecordingConfig = {
        selectedMicId: '', 
        selectedCamId: '', //by default is empty (only screen)
        selectedScreenRecording: true // by default is true.
    }

    // data required to media devices
    // Specification of que requirements we have  (video and audio only)
    // public readonly constraints: MediaStreamConstraints = {
    //     'video': true,
    //     'audio': true
    // };

    // protected devicesRequired: Array<PermissionName> = [
    //     'microphone' as PermissionName,
    //     'camera' as PermissionName
    // ]
    // protected devicesPermissions: any = {
    //     'microphone': null,
    //     'camera': null
    // }; 

    constructor(private recorderSrv: RecorderService){}

    ngOnInit(): void {

        if (this.isUnsupportedBrowser()){
            this.status = this.STATUS_UNSUPPORTED_BROWSER;
            
        } else {
        
            // get devices available to built options.
            this.getAvailableDevices();
            // here we check and set listener to detect the permissions on the user browser and any change on them
            // this.devicesRequired.forEach((device: PermissionName) => this.checkForPermission(device))

            // // DETECT CHANGES ON DEVICES CONNECTED OR PERMISSIONS GRANTED (NOT NEEDED)
            // navigator.mediaDevices.addEventListener("devicechange", (event) => {
            //     console.log(" devicechange HERE !!!")
            //     this.getAvailableDevices();
            // });
        }

    }

    onAudioSourcesSelect(source: any){
        // set audio config to start recording
        // console.log(" SELECTED SOURCE AUDIO ",source)
        this.recordConfig.selectedMicId = source.value;
    }

    onImageSourcesSelect(source: any){
        // set image config to start recording
        // console.log(" SELECTED SOURCE VIDEO ",source)
        this.recordConfig.selectedScreenRecording = source.value.indexOf('screen') != -1 ;
        this.recordConfig.selectedCamId = source.value.replace('screen','');
    }

    onAction() {
        // SEND CONFIGURATION HERE TO THE SERVICE TO START RECORD
        this.recorderSrv.startRecording(this.recordConfig, this.notificationData);
        // DEBUG function: to test browser mimetype
        // this.recorderSrv.testBrowserMimeTypesSupproted();
        this.action.emit(true);
    }

    onLeftAction() {
        this.leftButtonAction.emit(true);
    }
    // this fucntion controls either each ng-template we see
    protected initView():void {
        if(this.audioSources != null && this.videoSources !=null){
            this.status = this.STATUS_DEVICES_READY;
        }
    }

    /**
     * Check browser agent
     * @returns boolean true when browser is supported or false if not
     */
    protected isUnsupportedBrowser(): boolean {
        const userAgent = window.navigator.userAgent.toLowerCase();
        // console.error(" BROWSWWWWW AGENT ", userAgent);
        return !(/(chrome|opera|edg)/i.test(userAgent));
      }

    /**
     * This enumerate the devices connected to the system and create a list for both 
     *  - microphones: 'audioinput'
     *  - cameras: 'videoinput'
     */
    protected async getAvailableDevices() {
        try{
            // we need to FORCE THE BROWSER TO ASK for media permissions: audio here
            const streamaudio = await navigator.mediaDevices.getUserMedia({ audio: true });
            //force stop because here we will not record nothing.
            streamaudio?.getTracks().forEach(track => track.stop());
        } catch (err) {
            // in case of user's device or user's browser does not support the navigator.mediaDevices API.
            this.status = this.STATUS_DEVICES_ERROR;
            console.error("AUDIO DEVICES NOT AVAILABLE!", err);
        }
        try {
            // we need to FORCE THE BROWSER TO ASK for media permissions: video here
            const streamvideo = await navigator.mediaDevices.getUserMedia({ video: true });
            // to info:
            let setts = streamvideo?.getTracks()[0]?.getSettings();
            console.info("User Camera Settings ", setts)
            //force stop because here we will not record nothing.
            streamvideo?.getTracks().forEach(track => track.stop());
        } catch (err) {
            // in case of user's device or user's browser does not support the navigator.mediaDevices API.
            this.status = this.STATUS_DEVICES_ERROR;
            console.error("VIDEO DEVICES NOT AVAILABLE!", err);
        }
        try {
            // now we get devides available
            const devices = await navigator.mediaDevices.enumerateDevices();
            devices.forEach(device => {
                // console.debug("AVAILABLE DEVICE ===> ",device);
                if (device.kind === 'audioinput' && device.deviceId != '') {
                    this.microphones.push(device);
                } else if (device.kind === 'videoinput' && device.deviceId != '') {
                    this.cameras.push(device);
                }
            });
            this.updateAvailableVideoOptions();
            this.updateAvailableAudioOptions();

        } catch (err) {
            // in case of user's device or user's browser does not support the navigator.mediaDevices API.
            this.status = this.STATUS_DEVICES_ERROR;
            console.error("NO DEVICES AVAILABLE! ",err);
        }
    }

    /**
     * NOT USED because not needed, we ask allways: Set array of permissions to continue
     * @param type name of the device PermissionName we want to check
     *  - options are "camera" and  "microphone" by now
     */
    // protected async checkForPermission (type: PermissionName){
    //     navigator.permissions
    //         .query({
    //             name: type,
    //         })
    //         .then(permissionStatus => {

    //         // console.log("ON checkForPermission cams:",this.cameras, this.microphones)
    //             // Will be 'granted', 'denied' or 'prompt':
    //             this.devicesPermissions[type.toString()] = (permissionStatus.state == 'granted');
    //             // TODO: seems not to work Listen for changes to the permission state
    //             // permissionStatus.onchange = () => {
    //             //     this.devicesPermissions[type.toString()] = (permissionStatus.state == 'granted');
    //             // };
    //         });
    // };

    /**
     * Set videoSources to display on the form inputs the 
     * record sources as options available to choose
     * (permissions not needed by now)
     */
    protected updateAvailableVideoOptions() : void {
        // set list of items for video image
        // console.log("ON updateAvailableRecordOptions cams:",this.cameras)
        if(this.cameras.length > 0){ // && this.devicesPermissions['camera'] // not usedd permissions here
            this.videoSources = [...this.videoSources, ...this.cameras.map((cam: MediaDeviceInfo, idx: number) => {
                return {
                    title: "WEBCAM - "+cam.label, //TODO set screen alwais on title
                    value: cam.deviceId,
                    selected: idx == 0? true:false
                }
            })]
            // MIXED option 
            this.videoSources.push( {
                title: "SCREEN + WEBCAM ("+this.cameras[0].label+")",
                value: 'screen'+this.cameras[0].deviceId,
                selected: false
            })
        }
        
        console.debug(" Video SOURCES: ",this.videoSources)

        this.initView();
            
    }

    /**
     * Set audioSources to display on the form selectors the 
     * record sources as options available to choose
     */
    protected updateAvailableAudioOptions() : void {
        // set list of items for audio
        
        // console.log("ON updateAvailableRecordOptions mics:",this.microphones)
        // on FIREFOX MediaDeviceInfo objects have no label property
        if(this.microphones.length > 0){ // && this.devicesPermissions['microphone']
            this.audioSources = this.microphones.map((mic: MediaDeviceInfo, idx: number) => {
                return {
                    title: mic.label,
                    value: mic.deviceId,
                    selected: idx == 0? true:false
                }
            });
            this.recordConfig.selectedMicId = this.microphones[0].deviceId;
        } else {
            this.audioSources = []; // needed for checks
        }
       
        console.debug(" AUDIO SOURCES: ",this.audioSources)

        this.initView();
            
    }


}
